import React, { FC, useState, useRef } from 'react';
import { useDeviceInfo } from '../../../../hooks/useDeviceInfo';
import { Paragraph } from '../../../shared/Paragraph';
import { Prompt } from '../../../shared/Prompt';
import { ParsedPromo, PromoTypes } from '../types';
import { Input } from '../../../shared/Input';
import { useDispatch } from 'react-redux';
import { setCashierModal } from '../../../../state/cashier/reducers';
import { CashierType } from '../../../../state/cashier';
import { useTranslations } from '../../../../hooks/useTranslationsHelper';
import { formatCurrency } from '../../../../utils/wallets';
import { minDeposit } from '../consts';
import './PromotionMessagesPrompt.scss';

export type PromotionMessagesPromptProps = {
  imgMobilePath?: string;
  imgMobileAlt?: string;
  promoCode?: string;
  onClose: () => void;
  siteLink?: string;
  activatedPromo: ParsedPromo | null;
  loading?: boolean;
};

export const PromotionMessagesPrompt: FC<PromotionMessagesPromptProps> = ({
  onClose,
  imgMobilePath,
  imgMobileAlt,
  activatedPromo,
}): JSX.Element => {
  const { t } = useTranslations();
  const dispatch = useDispatch();
  const inputRef = useRef(null);

  const activatedPromoType = activatedPromo?.promoType?.toLowerCase();
  const isDeposit = activatedPromoType === PromoTypes.DEPOSIT_MATCH;
  const primaryBtnText = t((isDeposit && 'promotions.button.deposit.text') || 'promotions.opt-in');
  const defaultDeposit = Number(minDeposit);

  const isMobile = useDeviceInfo().isMobileDevice;

  const [depValue, setDepValue] = useState(0);
  const [inputError, setInputError] = useState('Please enter an amount');

  const handlePromptBtnClick = (): void => {
    dispatch(
      setCashierModal({
        isOpen: true,
        type: CashierType.Deposit,
        promoCode: String(activatedPromo?.dmBonusId),
        amount: depValue,
        promoTag: activatedPromo?.tagId as string,
      })
    );
    onClose();
  };

  const onDepValChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const inputValue = event.target.value;

    const validNumberRegex = /^[1-9][0-9]*$/;

    if (inputValue === '' || !validNumberRegex.test(inputValue)) {
      setInputError('Please enter a valid whole number');

      return;
    }

    const value = Number(inputValue);

    setDepValue(value);

    if (activatedPromo?.dmMaxAmount && value > Number(activatedPromo.dmMaxAmount)) {
      setInputError('Exceeds the maximum for this bonus');

      return;
    }

    if (activatedPromo?.dmMaxAmount && value < 10) {
      setInputError('Cannot be lower than minimum deposit amount');

      return;
    }

    setInputError('');
  };

  return (
    <Prompt
      elementToFocusOnOpen={inputRef}
      className="prompt--default"
      show
      title={activatedPromo?.title || ''}
      onClose={onClose}
      image={{
        src: String(isMobile && imgMobilePath ? imgMobilePath : activatedPromo?.imgPath),
        alt: (isMobile && imgMobileAlt) || activatedPromo?.imgAlt,
      }}
      PrimaryButton={{
        onClick: handlePromptBtnClick,
        text: primaryBtnText,
        disabled: inputError.length > 0,
      }}
      SecondaryButton={{
        onClick: onClose,
        text: t('buttons.cancel.uppercase'),
      }}
    >
      <Paragraph noMargin size="sm">
        {t('promotions.prompt.deposit.title')}
      </Paragraph>
      <Paragraph
        className="paragraph--strong"
        size="sm"
      >{`${formatCurrency(defaultDeposit, 'auto', 'auto')} - ${formatCurrency(Number(activatedPromo?.dmMaxAmount) || defaultDeposit)}`}</Paragraph>
      <Input
        ref={inputRef}
        validationMessages={[{ id: 'id', message: inputError }]}
        validationState={inputError.length ? 'error' : 'success'}
        type="number"
        theme="dark"
        onChange={onDepValChange}
        onBlur={onDepValChange}
        label=""
        step={1}
        inputMode="numeric"
      />
    </Prompt>
  );
};
